import 'antd/dist/reset.css';
import { ConfigProvider, notification } from 'antd';
import React, { useEffect } from 'react';
import moment from 'moment';

// Components & Routes
import { Router } from './routes';
import { Loading } from './components/common/Loading';

import { PopupProvider } from './components/common/Popup/PopupContext';
import SmartOTPModal from './components/smartotp/SmartOTPModal';

// Services & Utilities
import { setNotificationApi } from './utils/toast';
import { authService } from './services/auth.service';
import { registerOtpContextHandler } from './services/otp.service';

// Contexts
import { AuthProvider } from './layouts/AuthContext';
import { OtpProvider, useOtp } from './contexts/OtpContext';
import { usePreventSwipeNavigation } from './hooks/usePreventSwipeNavigation';

/**
 * Khai báo các biến global cho ứng dụng
 */
declare global {
  interface Window {
    /**
     * Đánh dấu đã khởi tạo auth (tránh gọi lại giữa các tab)
     */
    vfIsAuthInitialized?: boolean;
  }
}

/**
 * App là component chính của ứng dụng
 */
function App() {
  // Notification API của Ant Design
  usePreventSwipeNavigation();
  const [api, contextHolder] = notification.useNotification();
  const [authInitialized, setAuthInitialized] = React.useState(false);

  // Cài đặt API thông báo toàn cục
  useEffect(() => {
    setNotificationApi(api)
  }, [api]);
  
  /**
   * Lắng nghe sự kiện storage để reset biến khởi tạo khi đăng xuất
   * Giúp đồng bộ trạng thái đăng xuất giữa các tab
   */
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === '@USER_TOKEN' && !e.newValue) {
        console.log('Token removed, resetting auth initialization state');
        window.vfIsAuthInitialized = false;
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  /**
   * Khởi tạo xác thực người dùng khi ứng dụng khởi động
   */
  useEffect(() => {
    const initializeAuth = async () => {
      // Tránh khởi tạo trùng lặp trên nhiều tab
      if (window.vfIsAuthInitialized) {
        console.log('=== Auth already initialized in another tab, skipping initialization ===');
        setAuthInitialized(true);
        return;
      }
      
      console.log('=== Initializing auth ===');
      
      // Đọc trực tiếp dữ liệu từ localStorage
      const storedToken = localStorage.getItem('@USER_TOKEN');
      
      // deviceId và fcmToken được lấy trực tiếp từ localStorage khi cần
      
      // Log all localStorage items
      console.log('=== localStorage contents after auth init ===');
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          const value = localStorage.getItem(key);
          console.log(`${key}:`, value);
        }
      }
      
      // Hiển thị thông tin token đã được đọc
      console.log('Token from localStorage:', storedToken ? JSON.parse(storedToken) : null);
      
      // Nếu có token hợp lệ, preload dữ liệu để tránh AuthContext gọi API lại sau
      if (storedToken) {
        try {
          const tokenData = JSON.parse(storedToken);
          // Kiểm tra token hợp lệ và chưa hết hạn
          if (tokenData.access_token) {
            const tokenExpires = moment(tokenData['.expires']);
            const tokenExpired = moment().isAfter(tokenExpires);
            
            if (!tokenExpired) {
              console.log('Valid token found in localStorage, pre-loading cached data');
              
              // Kiểm tra và preload links từ cache nếu có
              const cachedLinks = authService.getHomeLinks();
              if (cachedLinks) {
                console.log('Using cached links in App initialization');
              } else {
                // Nếu chưa có cache, thử get links
                try {
                  console.log('No cached links found, fetching from API');
                  await authService.storeHomeLinks();
                } catch (error) {
                  console.error('Failed to fetch links during app init:', error);
                }
              }
            } else {
              console.log('Token expired, will attempt refresh in AuthContext');
            }
          }
        } catch (error) {
          console.error('Error parsing stored token:', error);
        }
      }
      
      // Preload initial data nếu có token
      if (storedToken) {
        try {
          await authService.checkAndLoadInitialData();
        } catch (error) {
          console.error('Failed to load initial data during app init:', error);
        }
      }
      
      console.log('=== Auth initialization complete ===');
      
      // Đánh dấu đã khởi tạo xong (cả global và local state)
      window.vfIsAuthInitialized = true;
      setAuthInitialized(true);
    };
    
    initializeAuth();
  }, []);

  return (
    <ConfigProvider theme={{
      token: {
        fontFamily: 'Helvetica Neue, sans-serif',
        colorPrimary: '#5A4099',
        colorPrimaryHover: '#E2E2E2', 
        colorPrimaryActive: '#E2E2E2', 
        colorBgContainer: '#ffffff', 
        colorLink: '#2E1962',
        borderRadius: 8, 
        colorSuccess: '#4ADE80', 
        colorWarning: '#FACC15', 
        colorError: '#F15B5B', 
      }
    }}>
      {contextHolder}
      
      {/* Hiển thị màn hình loading ban đầu khi đang khởi tạo auth */}
      {!authInitialized ? (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#ffffff'
        }}>
          {/* Sử dụng Loading component đã có sẵn trong dự án */}
          <Loading />
        </div>
      ) : (
        <PopupProvider>
            <OtpProvider>
              <AuthProvider>
                <Router />
                {/* Global SmartOTP Modal */}
                <GlobalSmartOTPModal />
                {/* OtpContext Handler Registration */}
                <OtpContextRegistration />
              </AuthProvider>
            </OtpProvider>
        </PopupProvider>
      )}
    </ConfigProvider>
  );
}

/**
 * GlobalSmartOTPModal Component
 * A global modal for OTP verification that can be shown from anywhere in the app
 */
const GlobalSmartOTPModal = () => {
  // Import from OTP context using the hook
  const { isOtpModalVisible, hideOtpModal, onOtpSuccess, onOtpError, fromScreen } = useOtp();

  // Handle OTP verification success
  const handleSuccess = () => {
    if (onOtpSuccess) {
      onOtpSuccess();
    }
    hideOtpModal();
  };

  // Handle OTP verification error
  const handleError = () => {
    if (onOtpError) {
      onOtpError();
    }
    hideOtpModal();
  };

  return (
    <SmartOTPModal
      visible={isOtpModalVisible}
      onClose={hideOtpModal}
      onSuccess={handleSuccess}
      onError={handleError}
      from={fromScreen}
    />
  );
};

/**
 * OtpContextRegistration Component
 * Đăng ký các hàm xử lý OTP context cho OtpService sử dụng
 */
const OtpContextRegistration = () => {
  // Lấy các hàm từ OTP context
  const { 
    showOtpModal, 
    hideOtpModal, 
    setOtpSuccessCallback, 
    setOtpErrorCallback,
    setFromScreen 
  } = useOtp();
  
  // Đăng ký handler khi component mount
  React.useEffect(() => {
    console.log('Registering global OTP context handler');
    registerOtpContextHandler({
      showOtpModal,
      hideOtpModal,
      setOtpSuccessCallback,
      setOtpErrorCallback,
      setFromScreen
    });
    
    // Không cần cleanup vì handler này sẽ tồn tại suốt vòng đời ứng dụng
  }, [showOtpModal, hideOtpModal, setOtpSuccessCallback, setOtpErrorCallback, setFromScreen]);
  
  // Component này không render bất kỳ UI nào
  return null;
};

export default App;
